// Busca no histórico, em todos os canais — Fase 14.
//
// Diferente da busca da coluna da esquerda, que olha nome, telefone, protocolo
// e a ÚLTIMA mensagem. Esta entra no corpo de tudo que já foi dito — é a que
// responde "onde a gente falou de troca de titularidade com esse cliente?".
//
// Ela roda numa função do banco, e não num `ilike` do PostgREST, por dois
// motivos que andam juntos: o índice GIN em português só é usado pela expressão
// exata (`ilike` varreria as 546 mil linhas de amanhã), e a RPC sem
// `security definer` mantém a RLS valendo — agente continua sem enxergar
// conversa que está com outra pessoa, inclusive pela busca.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Search, X } from 'lucide-react';
import type { AchadoBusca } from '../apiOrganizacao';
import { buscarMensagens } from '../apiOrganizacao';
import { carimboLista } from '../formato';

interface Props {
  orgId: string;
  onAbrirConversa: (conversationId: string) => void;
  onFechar: () => void;
}

const NOME_CANAL: Record<string, string> = {
  whatsapp_evolution: 'WhatsApp',
  whatsapp_cloud: 'WhatsApp',
  instagram: 'Instagram',
  email: 'E-mail',
};

/**
 * O trecho vem do banco com o termo entre « », do `ts_headline`.
 *
 * Marcar aqui no front pediria repetir a mesma análise linguística que o
 * Postgres já fez (radical de palavra, acento, stopword) — e uma marcação que
 * discordasse do que casou seria pior que nenhuma.
 */
function comDestaque(trecho: string) {
  return trecho.split(/(«[^»]*»)/g).map((parte, i) =>
    parte.startsWith('«') && parte.endsWith('»') ? (
      <mark key={i} className="rounded bg-secondary/40 px-0.5 text-gray-900 dark:text-white">
        {parte.slice(1, -1)}
      </mark>
    ) : (
      <span key={i}>{parte}</span>
    ),
  );
}

export default function ModalBusca({ orgId, onAbrirConversa, onFechar }: Props) {
  const [termo, setTermo] = useState('');
  const [achados, setAchados] = useState<AchadoBusca[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [buscou, setBuscou] = useState(false);
  const campo = useRef<HTMLInputElement>(null);

  useEffect(() => { campo.current?.focus(); }, []);

  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [onFechar]);

  const buscar = useCallback(async (t: string) => {
    if (t.trim().length < 2) {
      setAchados([]);
      setBuscou(false);
      return;
    }
    setBuscando(true);
    setErro(null);
    try {
      setAchados(await buscarMensagens(orgId, t));
      setBuscou(true);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setBuscando(false);
    }
  }, [orgId]);

  // Espera parar de digitar. Cada busca é uma consulta full-text; disparar por
  // tecla faria dez consultas para uma palavra de dez letras.
  useEffect(() => {
    const t = setTimeout(() => void buscar(termo), 350);
    return () => clearTimeout(t);
  }, [termo, buscar]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 px-4 pt-[8vh] backdrop-blur-sm"
      onClick={onFechar}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[75vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-slate-800"
      >
        <div className="flex shrink-0 items-center gap-2 border-b border-gray-200 px-4 py-3 dark:border-slate-700">
          <Search size={16} className="shrink-0 text-gray-400" />
          <input
            ref={campo}
            value={termo}
            onChange={(e) => setTermo(e.target.value)}
            placeholder="Buscar em todas as mensagens…"
            className="min-w-0 flex-1 bg-transparent text-sm text-gray-800 outline-none placeholder:text-gray-400 dark:text-slate-100"
          />
          {buscando && <Loader2 size={14} className="shrink-0 animate-spin text-gray-400" />}
          <button
            onClick={onFechar}
            className="shrink-0 rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 dark:hover:bg-slate-700"
          >
            <X size={15} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-2 py-2">
          {erro && (
            <p className="mx-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-300">
              {erro}
            </p>
          )}

          {!erro && termo.trim().length < 2 && (
            <p className="px-3 py-6 text-center text-xs text-gray-400 dark:text-slate-500">
              Digite pelo menos duas letras. A busca entra no corpo das mensagens de todos os
              canais — WhatsApp, e-mail e Instagram.
            </p>
          )}

          {!erro && buscou && achados.length === 0 && (
            <p className="px-3 py-6 text-center text-xs text-gray-400 dark:text-slate-500">
              Nada encontrado para “{termo}”. Só aparecem conversas que você pode ver.
            </p>
          )}

          {achados.map((a) => (
            <button
              key={a.message_id}
              onClick={() => { onAbrirConversa(a.conversation_id); onFechar(); }}
              className="w-full rounded-xl px-3 py-2 text-left transition-colors hover:bg-gray-50 dark:hover:bg-slate-700/50"
            >
              <div className="flex items-center gap-1.5">
                <span className="truncate text-xs font-semibold text-gray-800 dark:text-slate-100">
                  {a.contato_nome ?? 'Contato sem nome'}
                </span>
                <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 dark:bg-slate-700 dark:text-slate-400">
                  {NOME_CANAL[a.channel_type] ?? a.channel_type}
                </span>
                <span className="shrink-0 text-[10px] text-gray-400 dark:text-slate-500">
                  {a.direction === 'inbound' ? 'do cliente' : 'nossa'} · {carimboLista(a.provider_timestamp)}
                </span>
              </div>
              {a.assunto && (
                <p className="truncate text-[11px] font-medium text-gray-600 dark:text-slate-300">{a.assunto}</p>
              )}
              <p className="mt-0.5 line-clamp-2 text-xs leading-snug text-gray-500 dark:text-slate-400">
                {comDestaque(a.trecho)}
              </p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
